import { createContext, useCallback, useContext, useState } from 'react';

type TourContextType = {
  currentStep: number;
  tourCookieName: string | null;
  actions: {
    nextStep: () => void;
    previousStep: () => void;
    startTour: (onEndTour: () => void, tourName: string) => void;
    endTour: () => void;
  };
};

export const TourContextDefault: TourContextType = {
  currentStep: 0,
  tourCookieName: null,
  actions: {
    nextStep: () => {},
    previousStep: () => {},
    startTour: () => {},
    endTour: () => {},
  },
};

export const TourContext = createContext<TourContextType>(TourContextDefault);

export const TourProvider = ({ children }: { children: React.ReactNode }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [endTourCallback, setEndTourCallback] = useState<(() => void) | null>(
    null,
  );
  const [tourCookieName, setTourCookieName] = useState<string | null>(null);

  const nextStep = () => {
    setCurrentStep(prevStep => prevStep + 1);
  };

  const previousStep = () => {
    setCurrentStep(prevStep => Math.max(0, prevStep - 1));
  };

  const startTour = (onEndTour: () => void, tourName: string) => {
    setCurrentStep(1);
    setEndTourCallback(() => onEndTour);
    setTourCookieName(tourName);
  };

  const endTour = useCallback(() => {
    setCurrentStep(0);

    if (endTourCallback) {
      endTourCallback();
    }

    setEndTourCallback(null);
    setTourCookieName(null);
  }, [endTourCallback]);

  return (
    <TourContext.Provider
      value={{
        currentStep,
        tourCookieName,
        actions: { nextStep, previousStep, startTour, endTour },
      }}
    >
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  return useContext(TourContext);
};
